import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";

import { shareErrors, hasErrors } from "@/lib/memberForms";
import { writeOutcome } from "@/lib/community";
import { shareOptions, normalizeSharePreview, previewMatches, createdGrantId, dayTime } from "@/lib/v3Pass";
import { useMemberAuth } from "../MemberAuthContext";
import { useConfirmedContacts } from "../useContacts";
import { mutate } from "../actions";
import { ErrorNote } from "../components/kit";
import { V3Title, V3Columns, V3Check, V3Actions, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3_LINK } from "../components/v3app";
import { ShareAside, PrivateBanner, FieldCard, FIELD_INPUT } from "../components/v3pass";
import { usePass, passState, PassStateNote } from "./AccessPass";

const BANNER = "Empfänger, Zweck, ausgewählte Angaben und Dauer gehören zu dieser einen Freigabe.";
const CONSENT = "Diese ausgewählten Angaben für den angezeigten Zweck freigeben.";
const UNCLEAR = "Ob die Freigabe erstellt wurde, ist unklar. Bitte prüfe zuerst deine Freigaben, bevor du es noch einmal versuchst.";

function FieldPicker({ options, value, onChange, error, state }) {
  const [open, setOpen] = useState(false);
  const chosen = options.filter((o) => value.includes(o.id));
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <FieldCard id="share-fields" as="p" label="Ausgewählte Angaben" error={error} testid="share-fields-card">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="share-fields-list" aria-describedby="share-fields-label" data-testid="share-fields-toggle"
        className="flex w-full items-center justify-between gap-3 text-left text-[14.5px] leading-[22px] text-v3-ink lg:text-[16px]">
        <span className="min-w-0 break-words" data-testid="share-fields-summary">{chosen.length ? chosen.map((o) => o.label).join(" · ") : "Noch keine Angabe ausgewählt"}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-v3-muted transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <fieldset id="share-fields-list" className="mt-[12px] space-y-[8px] border-t border-v3-line pt-[12px]" data-testid="share-fields-list">
          <legend className="sr-only">Angaben zum Teilen</legend>
          {state !== "ok" ? <p className="text-[14px] text-v3-muted">Gespeicherte Angaben sind noch nicht verfügbar – es gibt nichts zum Auswählen.</p>
            : !options.length ? <p className="text-[14px] text-v3-muted">Du hast noch keine Angaben gespeichert. <Link to="/access-pass" className="font-bold text-v3-magenta hover:underline">Angaben bearbeiten</Link></p>
            : options.map((o) => (
              <label key={o.id} className="flex cursor-pointer items-start gap-[12px] text-[14px] text-v3-ink">
                <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} data-testid={`share-field-${o.id}`} className="mt-[2px] h-[20px] w-[20px] shrink-0 accent-[#D40A57]" />
                <span><span className="font-bold">{o.label}</span>: {o.value} <span className="text-v3-muted">· {o.section}</span></span>
              </label>))}
        </fieldset>
      )}
    </FieldCard>
  );
}

function ShareForm({ f, set, errors, contacts, options, state, busy, onCheck, note }) {
  return (<>
    <V3Title title="Access Pass teilen und widerrufen" intro="Du entscheidest über Person, Zweck, einzelne Angaben und Dauer einer Freigabe." testid="share-title" />
    <V3Columns layout="detail" aside={<ShareAside />} main={
      <form onSubmit={(e) => { e.preventDefault(); onCheck(); }} noValidate data-testid="share-form" data-m="M20">
        <PrivateBanner title="Gezielte persönliche Freigabe" text={BANNER} testid="share-banner" />
        <PassStateNote state={state} />
        <FieldCard id="share-recipient" label="Empfängerin / Empfänger" error={errors.recipient}>
          <select id="share-recipient" value={f.recipientId} onChange={(e) => set("recipientId")(e.target.value)} data-testid="share-recipient" className={`${FIELD_INPUT} cursor-pointer`}>
            <option value="">{contacts.list === null ? "Kontakte werden geladen …" : contacts.list.length ? "Bestätigten Kontakt wählen" : "Noch keine bestätigten Kontakte"}</option>
            {(contacts.list || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FieldCard>
        <FieldCard id="share-purpose" label="Zweck" error={errors.purpose}>
          <input id="share-purpose" value={f.purpose} onChange={(e) => set("purpose")(e.target.value)} placeholder="Zum Beispiel: Kaffeetreffen am Samstag" data-testid="share-purpose" className={FIELD_INPUT} />
        </FieldCard>
        <FieldPicker options={options} value={f.fields} onChange={set("fields")} error={errors.fields} state={state} />
        <FieldCard id="share-expires" label="Gültig bis" error={errors.expires} hint="Danach sieht die Person keine Werte mehr – auch nicht über einen alten Aufruf.">
          <input id="share-expires" type="datetime-local" value={f.expiresAt} onChange={(e) => set("expiresAt")(e.target.value)} data-testid="share-expires" className={FIELD_INPUT} />
        </FieldCard>
        <div className="mt-[18px]"><V3Check id="share-consent" checked={f.consent} onChange={set("consent")} error={errors.consent}>{CONSENT}</V3Check></div>
        {contacts.list && !contacts.list.length && <p className="text-[14px] text-v3-muted">Teilen ist nur mit bestätigten Kontakten möglich. <Link to="/menschen" className="font-bold text-v3-magenta hover:underline">Menschen entdecken</Link></p>}
        {note && <div className="mt-[12px]"><ErrorNote testid="share-error">{note}</ErrorNote></div>}
        <V3Actions>
          <button type="submit" disabled={state !== "ok" || busy} data-testid="share-check" className={V3_PRIMARY}>{busy ? "Vorschau wird erstellt …" : "Freigabe prüfen"}</button>
          <Link to="/access-pass" data-testid="share-cancel" className={V3_SECONDARY}>Abbrechen</Link>
        </V3Actions>
      </form>
    } />
  </>);
}

function ShareReview({ preview, onConfirm, onBack, busy, unclear, note }) {
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  return (<>
    <V3Title title="Access-Pass-Freigabe prüfen" intro="Kontrolliere diese eine Freigabe, bevor du sie bestätigst." testid="share-title" />
    <V3Columns layout="detail" aside={<ShareAside />} main={
      <div data-testid="share-review" data-m="M90">
        <PrivateBanner title="Gezielte persönliche Freigabe" text={BANNER} testid="share-banner" />
        <DetailList>
          <DetailRow label="Empfängerin / Empfänger" value={preview.recipient} testid="share-review-recipient" />
          <DetailRow label="Zweck" value={preview.purpose} testid="share-review-purpose" />
          <DetailRow label="Gewählte Angaben" value={preview.fields.join(" · ")} testid="share-review-fields" />
          <DetailRow label="Gültig bis" value={dayTime(preview.expiresAt)} testid="share-review-expires" />
        </DetailList>
        <div className="mt-[18px]"><V3Check id="share-confirm-consent" checked={consent} onChange={(v) => { setConsent(v); setError(""); }} error={error}>{CONSENT}</V3Check></div>
        {note && <div className="mt-[12px]"><ErrorNote testid="share-error">{note}</ErrorNote></div>}
        {unclear && <Link to="/access-pass/freigaben" className={`${V3_LINK} text-[14px]`} data-testid="share-check-grants">Meine Freigaben prüfen</Link>}
        <V3Actions>
          <button type="button" onClick={() => (consent ? onConfirm() : setError("Bitte bestätige diese Freigabe ausdrücklich."))} disabled={busy || unclear} data-testid="share-confirm" className={V3_PRIMARY}>{busy ? "Wird freigegeben …" : "Diese Freigabe bestätigen"}</button>
          <button type="button" onClick={onBack} disabled={busy} data-testid="share-review-cancel" className={V3_SECONDARY}>Abbrechen</button>
        </V3Actions>
      </div>
    } />
  </>);
}

// M20 / M90: the Core builds the exact preview (preview_hash); only that confirmed preview is sent (contract C.7).
function ShareFlow() {
  const navigate = useNavigate();
  const res = usePass();
  const contacts = useConfirmedContacts();
  const state = passState(res);
  const [f, setF] = useState({ recipientId: "", purpose: "", fields: [], expiresAt: "", consent: false });
  const [errors, setErrors] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [unclear, setUnclear] = useState(false);
  const set = (k) => (v) => { setF((x) => ({ ...x, [k]: v })); setErrors({}); setNote(""); };
  const check = async () => {
    const er = shareErrors(f);
    setErrors(er);
    if (hasErrors(er) || state !== "ok") return;
    const body = { recipient_member_id: f.recipientId, purpose: f.purpose.trim(), fields: f.fields, expires_at: new Date(f.expiresAt).toISOString() };
    setBusy(true);
    const r = await mutate("post", "/member/access-pass/shares/preview", body, "Die Vorschau wurde nicht erstellt");
    setBusy(false);
    if (r.rejected) { setNote(r.rejected); return; }
    const pv = normalizeSharePreview(r.data);
    if (!pv) { setNote("Die Vorschau konnte nicht geladen werden. Es wurde nichts geteilt."); return; }
    if (!previewMatches(pv, f)) { setNote("Die Vorschau des Servers weicht von deiner Auswahl ab. Es wurde nichts geteilt – bitte prüfe deine Angaben."); return; }
    setPreview({ ...pv, body });
  };
  const confirm = async () => {
    setBusy(true); setNote("");
    const r = await mutate("post", "/member/access-pass/shares", { ...preview.body, preview_hash: preview.hash }, "Die Freigabe wurde nicht erstellt");
    setBusy(false);
    const id = createdGrantId(r.data);
    const o = writeOutcome(r, id || null, UNCLEAR);
    if (o.state === "saved") { toast.success("Freigabe erstellt."); navigate(`/access-pass/freigaben/${encodeURIComponent(id)}`); return; }
    if (o.state === "unclear") setUnclear(true);
    setNote(o.error);
  };
  if (preview) return <ShareReview preview={preview} onConfirm={confirm} onBack={() => { setPreview(null); setNote(""); }} busy={busy} unclear={unclear} note={note} />;
  return <ShareForm f={f} set={set} errors={errors} contacts={contacts} options={shareOptions(state === "ok" ? res.data.pass : null)} state={state} busy={busy} onCheck={check} note={note} />;
}

// Keyed by account: nothing chosen for one account survives a switch.
export default function AccessPassShare() {
  const { member } = useMemberAuth();
  return <ShareFlow key={member?.id || "anon"} />;
}

